import { useQuery } from "@tanstack/react-query";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { LocaleLink } from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import SEOHead from "@/components/seo/SEOHead";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2,
  FileText,
  Download,
  ArrowLeft,
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
} from "lucide-react";

interface MolliePayment {
  id: string;
  description: string;
  amount: { value: string; currency: string };
  status: string;
  method: string | null;
  createdAt: string;
  paidAt: string | null;
  sequenceType: string;
  _links: {
    receipt: string | null;
    chargebackReceipt: string | null;
  };
}

async function fetchInvoices(accessToken: string): Promise<MolliePayment[]> {
  const res = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mollie-api`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ action: "get_invoices", limit: 100 }),
    }
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Fehler beim Laden");
  return data.payments || [];
}

const statusConfig: Record<string, { icon: typeof CheckCircle; className: string; labelKey: string }> = {
  paid: { icon: CheckCircle, className: "text-accent", labelKey: "invoices.statusPaid" },
  open: { icon: Clock, className: "text-primary", labelKey: "invoices.statusOpen" },
  pending: { icon: Clock, className: "text-muted-foreground", labelKey: "invoices.statusPending" },
  canceled: { icon: XCircle, className: "text-muted-foreground", labelKey: "invoices.statusCanceled" },
  expired: { icon: XCircle, className: "text-muted-foreground", labelKey: "invoices.statusExpired" },
  failed: { icon: AlertTriangle, className: "text-destructive", labelKey: "invoices.statusFailed" },
};

const ProviderInvoices = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { profile, isLoadingProfile } = useProvider();

  const { data: payments, isLoading } = useQuery({
    queryKey: ["provider-invoices", profile?.id],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Nicht angemeldet");
      return fetchInvoices(session.access_token);
    },
    enabled: !!profile?.id,
  });

  if (authLoading || isLoadingProfile || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  // Filter out setup payments (0.01 EUR)
  const displayPayments = (payments || []).filter(
    (p) => !(p.amount.value === "0.01" && p.description?.includes("Einrichtung"))
  );

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={t("invoices.title")} noindex canonical="/provider/invoices" />
      <Header />

      <main className="pt-24 pb-12">
        <div className="section-padding border-b border-border">
          <div className="max-w-5xl mx-auto py-6">
            <div className="flex items-center gap-4 mb-2">
              <LocaleLink to="/provider/billing" className="text-muted-foreground hover:text-foreground transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </LocaleLink>
              <h1 className="text-headline">{t("invoices.title")}</h1>
            </div>
            <p className="text-caption ml-9">{t("invoices.subtitle")}</p>
          </div>
        </div>

        <div className="section-padding">
          <div className="max-w-5xl mx-auto mt-8">
            {displayPayments.length === 0 ? (
              <div className="text-center py-16 space-y-4">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto" />
                <h2 className="text-subheadline">{t("invoices.empty")}</h2>
                <p className="text-caption max-w-md mx-auto">{t("invoices.emptyDesc")}</p>
              </div>
            ) : (
              <div className="border border-border bg-card overflow-hidden">
                {/* Desktop Table Header */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-secondary/30 text-xs text-muted-foreground uppercase tracking-wider border-b border-border">
                  <div className="col-span-2">{t("invoices.colDate")}</div>
                  <div className="col-span-4">{t("invoices.colDescription")}</div>
                  <div className="col-span-2 text-right">{t("invoices.colAmount")}</div>
                  <div className="col-span-2">{t("invoices.colStatus")}</div>
                  <div className="col-span-2">{t("invoices.colMethod")}</div>
                </div>

                {displayPayments.map((payment) => {
                  const cfg = statusConfig[payment.status] || statusConfig.pending;
                  const StatusIcon = cfg.icon;
                  const date = new Date(payment.createdAt);

                  return (
                    <div
                      key={payment.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-4 border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors"
                    >
                      {/* Mobile stacked / Desktop grid */}
                      <div className="md:col-span-2 text-sm">
                        <span className="md:hidden text-xs text-muted-foreground mr-2">{t("invoices.colDate")}:</span>
                        {date.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}
                      </div>
                      <div className="md:col-span-4 text-sm truncate">
                        <span className="md:hidden text-xs text-muted-foreground mr-2">{t("invoices.colDescription")}:</span>
                        {payment.description}
                      </div>
                      <div className="md:col-span-2 text-sm md:text-right font-medium">
                        <span className="md:hidden text-xs text-muted-foreground mr-2">{t("invoices.colAmount")}:</span>
                        {parseFloat(payment.amount.value).toFixed(2)} {payment.amount.currency}
                      </div>
                      <div className="md:col-span-2 flex items-center gap-1.5">
                        <StatusIcon className={`w-3.5 h-3.5 ${cfg.className}`} />
                        <span className={`text-sm ${cfg.className}`}>{t(cfg.labelKey)}</span>
                      </div>
                      <div className="md:col-span-2 flex items-center justify-between">
                        <span className="text-sm text-muted-foreground capitalize">
                          {payment.method || "—"}
                        </span>
                        {payment._links.receipt && (
                          <a
                            href={payment._links.receipt}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:text-accent/80 transition-colors"
                            title={t("invoices.download")}
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProviderInvoices;
